import { API } from '@/api'
import type {
  CreateDemandPayload,
  Demand,
  InterestedSib,
  MarketplaceDemand,
  MatchingSib,
  RejectDemandPayload,
  MatchingSibsResponse,
  TrafficLightColor,
  SibCapacityDetails,
  SibInterestStatus,
} from './types'

const USE_MOCK_MARKETPLACE = false

// Demand state (interest/rejection counts, status, allocations) changes through actions of
// other users, so these reads must skip the shared 30s GET cache in `@/api` — otherwise a
// detail page or list re-fetched after an action would still show the stale cached counts.
const NO_CACHE: RequestInit = { headers: { 'x-disable-cache': '1' } }

type MarketplaceDemandsResponse = {
  items: MarketplaceDemand[]
}

function mapTrafficLightStatus(value: 'GREEN' | 'YELLOW' | 'RED'): TrafficLightColor {
  switch (value) {
    case 'GREEN':
      return 'green'
    case 'YELLOW':
      return 'yellow'
    case 'RED':
      return 'red'
  }
}

type InterestedSibsResponse = {
  items: Array<{
    userId: string
    userName: string
    status: string
    rejectionReason: string | null
    rejectionDetails: string | null
    updatedAt: string
  }>
}

export type BankDetailsResponse = {
  id: number
  postal?: string | null
  city?: string | null
  country?: string | null
  salesRegion?: string | null
}

export function getBankDetails(bankId: string | number) {
  return API<BankDetailsResponse>(
    `/settings/banks/${bankId}`,
    {},
    'Could not load bank details.',
    'BANK_DETAILS_LOAD_FAILED'
  )
}

function buildDemandUpdatePayload(
  demand: Demand,
  visibilityMode: 'SPECIFIC_USERS' | 'ALL_USERS',
  selectedUserIds: string[]
) {
  return {
    customerId: demand.customerId,
    buckets: demand.buckets,

    periodStart: demand.periodStart,
    periodEnd: demand.periodEnd,
    postal: demand.postal,
    city: demand.city,
    country: demand.country,
    salesRegion: demand.salesRegion,

    topic: demand.topic,
    description: demand.description,

    visibilityMode,
    selectedUserIds,
    periodRangeValid: true,
    visibilitySelectionValid: true,
  }
}

export async function getUnionDemands(isArchived = false) {
  const result = await API<MarketplaceDemandsResponse>(
    `/marketplace-demands?isArchived=${isArchived}`,
    NO_CACHE,
    'Could not load marketplace demands.',
    'MARKETPLACE_DEMANDS_LOAD_FAILED'
  )

  return result.items
}

export function createDemand(payload: CreateDemandPayload) {
  if (USE_MOCK_MARKETPLACE) {
    return Promise.reject(new Error('Mock createDemand is no longer maintained'))
  }

  return API<Demand>(
    '/marketplace-demands',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Could not create marketplace demand.',
    'MARKETPLACE_DEMAND_CREATE_FAILED'
  )
}

export function getDemand(demandId: string | number) {
  return API<Demand>(
    `/marketplace-demands/${demandId}`,
    NO_CACHE,
    'Could not load marketplace demand.',
    'MARKETPLACE_DEMAND_LOAD_FAILED'
  )
}

export async function getMatchingSibs(demandId: string | number): Promise<MatchingSib[]> {
  const result = await API<MatchingSibsResponse>(
    `/marketplace-demands/${demandId}/match`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
    'Could not load matching SIBs.',
    'MARKETPLACE_MATCHING_SIBS_LOAD_FAILED'
  )

  const enriched = (result.matchedSibs ?? []).map(sib => ({
    id: sib.userId,
    name: sib.name,
    postalCode: sib.postal ?? undefined,
    city: sib.city ?? undefined,
    salesRegion: sib.salesRegion ?? undefined,
    workRadius: sib.workRadius ?? undefined,
    workingArea: sib.workingArea ?? undefined,
    trafficLightStatus: mapTrafficLightStatus(sib.trafficLightStatus),

    remainingCapacity: sib.overview?.map(service => ({
      serviceType: service.serviceCode,
      units: service.remainingCapacity ?? 0,
    })),

    orderedUnits: sib.overview?.map(service => ({
      serviceType: service.serviceCode,
      ordered: service.orderedUnits ?? 0,
      completed: service.doneUnits ?? 0,
    })),

    topics: sib.topics ?? [],
  }))

  const priority: Record<string, number> = {
    GREEN: 0,
    green: 0,
    YELLOW: 1,
    yellow: 1,
    RED: 2,
    red: 2,
    unknown: 3,
  }

  return enriched.sort((a, b) => {
    const aRank = priority[a.trafficLightStatus ?? 'unknown']
    const bRank = priority[b.trafficLightStatus ?? 'unknown']

    if (aRank !== bRank) return aRank - bRank

    return a.name.localeCompare(b.name)
  })
}

export async function publishToSelectedSibs(demand: Demand, sibIds: string[]) {
  await API<void>(
    `/marketplace-demands/${demand.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(buildDemandUpdatePayload(demand, 'SPECIFIC_USERS', sibIds)),
    },
    'Could not update selected SIBs.',
    'MARKETPLACE_UPDATE_SELECTED_FAILED'
  )

  return API<void>(
    `/marketplace-demands/${demand.id}/publish`,
    { method: 'POST' },
    'Could not publish demand to selected SIBs.',
    'MARKETPLACE_PUBLISH_SELECTED_FAILED'
  )
}

export async function publishToAllMatchingSibs(demand: Demand) {
  await API<void>(
    `/marketplace-demands/${demand.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(buildDemandUpdatePayload(demand, 'ALL_USERS', [])),
    },
    'Could not update marketplace demand.',
    'MARKETPLACE_UPDATE_ALL_FAILED'
  )

  return API<void>(
    `/marketplace-demands/${demand.id}/publish`,
    { method: 'POST' },
    'Could not publish demand to matching SIBs.',
    'MARKETPLACE_PUBLISH_ALL_FAILED'
  )
}

export function updateDemand(demand: Demand) {
  // A plain edit must not change who can see the demand: published demands
  // are editable while no interest exists (SIB-924), and forcing ALL_USERS
  // here would silently broadcast a SPECIFIC_USERS demand to every SIB.
  const visibilityMode =
    demand.visibilityMode === 'SPECIFIC_USERS' ? 'SPECIFIC_USERS' : 'ALL_USERS'

  // The backend replaces the recipient list with what this request carries.
  // selectedUserIds is optional on the type, so refuse rather than PUT an
  // empty list and silently drop every selected recipient.
  if (visibilityMode === 'SPECIFIC_USERS' && !Array.isArray(demand.selectedUserIds)) {
    return Promise.reject(
      new Error(
        'Die Empfängerauswahl des Bedarfs ist nicht geladen — bitte die Seite neu laden und erneut versuchen.'
      )
    )
  }

  return API<Demand>(
    `/marketplace-demands/${demand.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(
        buildDemandUpdatePayload(demand, visibilityMode, demand.selectedUserIds ?? [])
      ),
    },
    'Could not update marketplace demand.',
    'MARKETPLACE_DEMAND_UPDATE_FAILED'
  )
}

export function deleteDemand(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}`,
    {
      method: 'DELETE',
    },
    'Could not delete marketplace demand.',
    'MARKETPLACE_DEMAND_DELETE_FAILED'
  )
}

export function archiveDemand(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}/archive`,
    { method: 'POST' },
    'Could not archive marketplace demand.',
    'MARKETPLACE_DEMAND_ARCHIVE_FAILED'
  )
}

function mapInterestStatus(status: string): SibInterestStatus {
  switch (status) {
    case 'INTERESTED':
      return 'interested'

    case 'REJECTED':
      return 'rejected'

    case 'WITHDRAWN':
      return 'withdrawn'

    case 'CONFIRMED':
      return 'confirmed'

    default:
      return 'none'
  }
}

export async function getInterestedSibs(demandId: string | number): Promise<InterestedSib[]> {
  const result = await API<InterestedSibsResponse>(
    `/marketplace-demands/${demandId}/interests`,
    NO_CACHE,
    'Could not load interested SIBs.',
    'MARKETPLACE_INTERESTS_LOAD_FAILED'
  )

  const year = new Date().getFullYear()

  return Promise.all(
    (result.items ?? []).map(async item => {
      const details = await getSibCapacityDetails(item.userId, year)

      return {
        id: item.userId,
        name: item.userName ?? details.fullName,
        postalCode: details.postal ?? undefined,
        city: details.city ?? undefined,
        interestStatus: mapInterestStatus(item.status),
        rejectionReasonText: item.rejectionDetails ?? undefined,
        topics: details.topics ?? [],
        remainingCapacity: details.services?.map(service => ({
          serviceType: service.serviceCode,
          units: service.remainingCapacity ?? 0,
        })),

        orderedUnits: details.services?.map(service => ({
          serviceType: service.serviceCode,
          ordered: service.orderedUnits ?? 0,
          completed: service.doneUnits ?? 0,
        })),
      }
    })
  )
}

export function confirmInterestedSibs(
  demandId: string | number,
  assignments: Array<{
    sibId: string
    serviceType: string
    units: number
  }>
) {
  const selectedInterestedUserIds = [...new Set(assignments.map(item => item.sibId))]

  const allocations = selectedInterestedUserIds.map(userId => ({
    userId,
    buckets: assignments
      .filter(item => item.sibId === userId)
      .map(item => ({
        serviceType: item.serviceType,
        allocatedUnits: item.units,
      })),
  }))

  return API<void>(
    `/marketplace-demands/${demandId}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({
        selectedInterestedUserIds,
        allocations,
      }),
    },
    'Could not confirm SIBs.',
    'MARKETPLACE_CONFIRM_FAILED'
  )
}

export function rejectInterestedSibs(demandId: string | number, userIds: string[]) {
  return API<void>(
    `/marketplace-demands/${demandId}/interests/reject`,
    {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    },
    'Could not reject interested SIBs.',
    'MARKETPLACE_REJECT_INTERESTS_FAILED'
  )
}

export function markDemandNotMatchable(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}/not-matchable`,
    { method: 'POST' },
    'Could not mark demand as not matchable.',
    'MARKETPLACE_NOT_MATCHABLE_FAILED'
  )
}

export async function getMarketplaceDemands(isArchived = false) {
  const result = await API<MarketplaceDemandsResponse>(
    `/marketplace-demands?isArchived=${isArchived}`,
    NO_CACHE,
    'Could not load marketplace demands.',
    'MARKETPLACE_DEMANDS_LOAD_FAILED'
  )

  return result.items
}

export function getMarketplaceDemand(demandId: string | number) {
  return API<MarketplaceDemand>(
    `/marketplace-demands/${demandId}`,
    NO_CACHE,
    'Could not load marketplace demand.',
    'MARKETPLACE_DEMAND_LOAD_FAILED'
  )
}

export function acceptDemand(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}/interest`,
    { method: 'POST' },
    'Could not register interest.',
    'MARKETPLACE_INTEREST_FAILED'
  )
}

export function rejectDemand(demandId: string | number, payload: RejectDemandPayload) {
  return API<void>(
    `/marketplace-demands/${demandId}/reject`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Could not reject offer.',
    'MARKETPLACE_REJECT_FAILED'
  )
}

export function withdrawInterest(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}/withdraw-interest`,
    { method: 'POST' },
    'Could not withdraw interest.',
    'MARKETPLACE_WITHDRAW_FAILED'
  )
}

export type UnseenMarketplaceDemands = {
  hasUnseen: boolean
  count: number
  unseenDemandIds: number[]
}

const NO_UNSEEN_DEMANDS: UnseenMarketplaceDemands = {
  hasUnseen: false,
  count: 0,
  unseenDemandIds: [],
}

/**
 * Tolerates a partial or malformed payload: the badge is decorative, so anything we cannot
 * read is treated as "nothing unseen" rather than surfaced as an error. `hasUnseen` is
 * derived from the ids when the backend omits it, so the dot and the list cannot disagree.
 */
export function normalizeUnseenMarketplaceDemands(raw: unknown): UnseenMarketplaceDemands {
  if (!raw || typeof raw !== 'object') return NO_UNSEEN_DEMANDS

  const body = raw as Partial<UnseenMarketplaceDemands>

  // Guard the types before coercing: Number(null) and Number('') are both 0, so mapping
  // first would turn holes in the payload into a demand with id 0.
  const unseenDemandIds = Array.isArray(body.unseenDemandIds)
    ? body.unseenDemandIds
        .filter(id => typeof id === 'number' || (typeof id === 'string' && id.trim() !== ''))
        .map(Number)
        .filter(id => Number.isFinite(id))
    : []

  const count = Number.isFinite(body.count) ? Number(body.count) : unseenDemandIds.length

  return {
    hasUnseen: typeof body.hasUnseen === 'boolean' ? body.hasUnseen : unseenDemandIds.length > 0,
    count: Math.max(0, count),
    unseenDemandIds,
  }
}

export async function getUnseenMarketplaceDemands(): Promise<UnseenMarketplaceDemands> {
  const result = await API<unknown>(
    '/marketplace-demands/unseen',
    // Polled endpoint — see NO_CACHE.
    NO_CACHE,
    'Could not load unseen marketplace demands.',
    'MARKETPLACE_UNSEEN_LOAD_FAILED'
  )

  return normalizeUnseenMarketplaceDemands(result)
}

export function markMarketplaceDemandSeen(demandId: string | number) {
  return API<void>(
    `/marketplace-demands/${demandId}/seen`,
    { method: 'POST' },
    'Could not mark marketplace demand as seen.',
    'MARKETPLACE_DEMAND_SEEN_FAILED'
  )
}

export function getSibCapacityDetails(userId: string, year: number) {
  return API<SibCapacityDetails>(
    `/capacity/sibs/${userId}?year=${year}`,
    {},
    'Could not load SIB capacity details.',
    'SIB_CAPACITY_DETAILS_LOAD_FAILED'
  )
}

export type SibProfileResponse = {
  userId: string
  topics?: Array<{
    code: string
    nameDe: string
  }>
}

export type WorkloadResponse = {
  buckets: Array<{
    serviceType: string
    remainingCapacityHours: number
  }>
}

export function getOwnProfile() {
  return API<SibProfileResponse>(
    '/settings/profile',
    {},
    'Could not load profile.',
    'PROFILE_LOAD_FAILED'
  )
}

export function getOwnWorkload(userId: string, year: number) {
  return API<WorkloadResponse>(
    `/orders/workload?userId=${userId}&from=${year}-01-01T00:00:00&to=${year}-12-31T23:59:59`,
    {},
    'Could not load workload.',
    'WORKLOAD_LOAD_FAILED'
  )
}
